/* Settings.
 *
 * THE AUTOMATION COPY IS THE DOCTRINE, not a feature list. Each line says what the
 * product actually does and who it comes from, because "Day 2 missed workout" as a
 * toggle label tells a gym owner nothing about whether they want it on.
 *
 * SENDING IS NOT A TOGGLE THE GYM OWNS. Acrostic switches it on once the domain is
 * verified and a number is provisioned. The switch is shown, off, and says why: a
 * hidden control is a question the owner then has to ask somebody.
 */
import { useState } from 'react';
import type { Role } from '@acrostic/model';
import { useAsync, useSource } from '../data/SourceProvider.js';
import { NoneRow, Panel } from '../components/Panel.js';
import { useToast } from '../components/Toast.js';
import { ACCOUNT_ROLES } from '../data/SeedSource.js';
import { COMMERCIALS_DEFAULT, canSetVisibility, seesCommercials } from '../seats.js';
import type { ScreenProps } from '../App.js';

interface Auto { key: string; title: string; desc: string }

const AUTOMATIONS: Auto[] = [
  {
    key: 'lead60sResponse', title: 'First response within the hour',
    desc: 'From a named person, inside the lead track’s first hour. Nobody hears from a robot first.',
  },
  {
    key: 'day2MissedWorkout', title: 'Going quiet',
    desc: 'A notice, then curiosity, then an invitation, each from their anchor. Never a discount first.',
  },
  {
    key: 'onboarding90d', title: 'First 90 days',
    desc: 'Day 1, 7, 21, 45 and 90 milestones, with a touch every few days between, each met on evidence.',
  },
  {
    key: 'winback90d', title: 'Win-back sequences',
    desc: '30, 90 and 180-day notes to former members, from whoever knew them best.',
  },
];

export function Settings({ user }: ScreenProps) {
  const source = useSource();
  const toast = useToast();
  const [epoch, setEpoch] = useState(0);
  const { data, error } = useAsync(() => source.getSession(), [source, epoch]);

  if (error) return <NoneRow>Could not read the settings. {error.message}</NoneRow>;
  if (!data) return <NoneRow>Reading…</NoneRow>;

  const gym = data.gym;
  const s = gym.settings;
  const autos = (s['automations'] ?? {}) as Record<string, boolean>;

  async function saveAuto(key: string, value: boolean) {
    try {
      await source.updateSettings({ automations: { ...autos, [key]: value } });
      setEpoch((n) => n + 1);
      toast('Saved', 'good');
    } catch (e) {
      toast((e as Error).message, 'bad');
    }
  }

  async function setVisibility(role: Role, on: boolean) {
    const now = (s['canSeeCommercials'] as Role[] | undefined) ?? COMMERCIALS_DEFAULT;
    const next = on ? [...new Set([...now, role])] : now.filter((r) => r !== role);
    try {
      await source.updateSettings({ canSeeCommercials: next });
      setEpoch((n) => n + 1);
      toast('Saved', 'good');
    } catch (e) {
      toast((e as Error).message, 'bad');
    }
  }

  async function trySending(key: 'emailSending' | 'smsSending') {
    try {
      await source.updateSettings({ [key]: true });
      setEpoch((n) => n + 1);
    } catch (e) {
      toast((e as Error).message, 'bad');
    }
  }

  return (
    <div className="grid2">
      <Panel title="Gym profile">
        <div className="deflist">
          {([
            ['Gym name', gym.name],
            ['Location', gym.location ?? '—'],
            ['Acrostic plan', String(s['plan'] ?? '—')],
            ['Member target', String(s['memberTarget'] ?? '—')],
          ] as [string, string][]).map(([k, v]) => (
            <div className="defrow" key={k}>
              <span className="t-caption muted">{k}</span>
              <span className="t-body-small">{v}</span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Message sending">
        <p className="notice warn t-body-small">
          Sending is off for every channel. Campaigns and reach-outs are rendered and
          logged, and nothing is delivered.
        </p>
        <Toggle
          on={!!s['emailSending']} title="Email sending"
          desc="Switched on by Acrostic once your domain is set up for delivery."
          onChange={() => void trySending('emailSending')}
        />
        <Toggle
          on={!!s['smsSending']} title="SMS sending"
          desc="Switched on by Acrostic once a number is provisioned and registered."
          onChange={() => void trySending('smsSending')}
        />
      </Panel>

      <Panel title="Automations">
        {AUTOMATIONS.map((a) => (
          <Toggle
            key={a.key} on={!!autos[a.key]} title={a.title} desc={a.desc}
            onChange={(v) => void saveAuto(a.key, v)}
          />
        ))}
      </Panel>

      {/* WHO SEES WHAT A MEMBER PAYS. An owner's call about their own staff, so a
          manager sees the panel and cannot change it: a manager who can grant
          themselves nothing new could still grant it to the floor. */}
      <Panel eyebrow="An owner's call" title="Who sees what a member pays">
        <p className="t-body-small muted" style={{ marginBottom: 6 }}>
          Plans, prices and renewal dates. Off for the floor by default.
        </p>
        {ACCOUNT_ROLES.map((r) => {
          const on = seesCommercials([r.key], s);
          const locked = r.key === 'gym_admin' || !canSetVisibility(user.roles);
          return (
            <label className={'togglerow' + (locked ? ' locked' : '')} key={r.key}>
              <input
                type="checkbox" checked={on} disabled={locked}
                onChange={(e) => void setVisibility(r.key, e.target.checked)}
              />
              <span>
                <span className="t-label-large">{r.label}</span>
                <span className="t-body-small muted">
                  {r.key === 'gym_admin'
                    ? 'The owner always sees them. Somebody has to.'
                    : on
                      ? 'Sees the plan, the price and the renewal date on every member.'
                      : 'Sees every member the same way: no plan, no price, no renewal.'}
                </span>
              </span>
            </label>
          );
        })}
        {!canSetVisibility(user.roles) && (
          <p className="t-caption muted" style={{ marginTop: 10 }}>
            Only the owner changes this.
          </p>
        )}
      </Panel>

      <Panel title="Demo data">
        <p className="t-body-small muted">
          This is a generated gym: 287 members, a pipeline and a year of attendance, all
          dealt from one seed. Nothing here is a real person.
        </p>
        <p className="t-body-small muted" style={{ marginTop: 8 }}>
          Reloading the page rebuilds it, so anything you change while exploring is
          discarded and the same gym comes back with the same people.
        </p>
      </Panel>
    </div>
  );
}

function Toggle({ on, title, desc, onChange }: {
  on: boolean; title: string; desc: string; onChange: (v: boolean) => void;
}) {
  return (
    <label className="togglerow">
      <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="t-label-large">{title}</span>
        <span className="t-body-small muted">{desc}</span>
      </span>
    </label>
  );
}
